import { useEffect, useMemo, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { Button } from '@/components/ui/button';
import {
  MapPin,
  Phone,
  MessageCircle,
  Clock,
  ArrowRight,
  Navigation,
  Instagram,
  Facebook,
  Youtube,
  Mail,
  Linkedin,
  Twitter,
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import {
  formatOpeningHoursDayRange,
  getOpeningHoursDay,
  hasOpeningHoursData,
} from '@/lib/openingHours';
import { Layout3Root } from './Layout3Root';
import './styles.css';
import { openStoreDirections } from '@/lib/storeMapUrls';

const WEEK_DAYS = [
  { key: 'monday', label: 'Segunda', short: 'Seg' },
  { key: 'tuesday', label: 'Terça', short: 'Ter' },
  { key: 'wednesday', label: 'Quarta', short: 'Qua' },
  { key: 'thursday', label: 'Quinta', short: 'Qui' },
  { key: 'friday', label: 'Sexta', short: 'Sex' },
  { key: 'saturday', label: 'Sábado', short: 'Sáb' },
  { key: 'sunday', label: 'Domingo', short: 'Dom' },
] as const;

const DAY_KEYS_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

type DayKey = (typeof WEEK_DAYS)[number]['key'];

type DaySchedule = {
  key: DayKey;
  label: string;
  short: string;
  hours: string;
  isClosed: boolean;
  isToday: boolean;
};

type StoreStatus = {
  isOpen: boolean;
  label: string;
  detail: string;
};

type Store = {
  _id?: string;
  company?: string;
  frontage?: string;
  phone?: { ios?: string; formatted?: string };
  whatsapp?: { ios?: string; formatted?: string };
  place?: { address?: string; loc?: number[] };
  openingHours?: { default?: Record<string, { from?: string; to?: string }> };
};

const parseTimeToMinutes = (time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
};

const getCurrentDayKey = (): DayKey => DAY_KEYS_BY_INDEX[new Date().getDay()] as DayKey;

const getDayHours = (openingHours: Store['openingHours'], dayKey: string) =>
  getOpeningHoursDay(openingHours, dayKey);

const getStoreStatus = (openingHours: Store['openingHours']): StoreStatus => {
  const todayKey = getCurrentDayKey();
  const todayHours = getDayHours(openingHours, todayKey);
  const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  if (todayHours?.from && todayHours?.to) {
    const from = parseTimeToMinutes(todayHours.from);
    const to = parseTimeToMinutes(todayHours.to);

    if (currentMinutes >= from && currentMinutes < to) {
      return { isOpen: true, label: 'Aberto agora', detail: `Até às ${todayHours.to}` };
    }
    if (currentMinutes < from) {
      return { isOpen: false, label: 'Fechado agora', detail: `Abrimos hoje às ${todayHours.from}` };
    }
  }

  const todayIndex = WEEK_DAYS.findIndex((d) => d.key === todayKey);
  for (let offset = 1; offset <= 7; offset += 1) {
    const nextDay = WEEK_DAYS[(todayIndex + offset) % 7];
    const nextHours = getDayHours(openingHours, nextDay.key);
    if (nextHours?.from && nextHours?.to) {
      const prefix = offset === 1 ? 'Amanhã' : nextDay.label;
      return { isOpen: false, label: 'Fechado agora', detail: `${prefix} às ${nextHours.from}` };
    }
  }

  return { isOpen: false, label: 'Fechado agora', detail: 'Veja os horários abaixo' };
};

const formatOpeningHours = (openingHours: Store['openingHours']): DaySchedule[] => {
  const todayKey = getCurrentDayKey();
  return WEEK_DAYS.map((day) => {
    const range = formatOpeningHoursDayRange(openingHours, day.key);
    const isClosed = range === 'Fechado';
    return {
      key: day.key,
      label: day.label,
      short: day.short,
      hours: isClosed ? 'Fechado' : range.replace(' - ', ' – '),
      isClosed,
      isToday: day.key === todayKey,
    };
  });
};

const openMapsDirections = (store: Store) => {
  const place = store.place;
  openStoreDirections(place);
};

const OpeningHoursWeek = ({ schedules, status }: { schedules: DaySchedule[]; status: StoreStatus }) => (
  <div className="wl3-hours-panel">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
      <div className="flex items-center gap-3">
        <div className="wl3-trust-icon shrink-0" style={{ backgroundColor: 'var(--wl3-accent-bg)' }}>
          <Clock className="h-5 w-5" style={{ color: 'var(--wl3-accent)' }} />
        </div>
        <div>
          <h3 className="font-semibold text-[var(--wl3-text)]">Horários</h3>
          <p className="text-sm text-[var(--wl3-muted)]">{status.detail}</p>
        </div>
      </div>
      <div className={`wl3-hours-status shrink-0 ${status.isOpen ? 'wl3-hours-status--open' : 'wl3-hours-status--closed'}`}>
        <span className="wl3-hours-status-dot" aria-hidden="true" />
        {status.label}
      </div>
    </div>

    <div className="wl3-hours-week" role="list" aria-label="Horários por dia">
      {schedules.map((schedule) => (
        <div
          key={schedule.key}
          role="listitem"
          className={`wl3-hours-week-day ${schedule.isToday ? 'wl3-hours-week-day--today' : ''}`}
          aria-current={schedule.isToday ? 'date' : undefined}
        >
          <span className="wl3-hours-week-day-label">{schedule.short}</span>
          {schedule.isToday && status.isOpen && (
            <span className="wl3-hours-week-live">
              <span className="wl3-hours-week-live-dot" aria-hidden="true" />
              Aberto
            </span>
          )}
          {schedule.isToday && !status.isOpen && (
            <span className="wl3-hours-week-day-label" style={{ color: '#b91c1c' }}>Hoje</span>
          )}
          <span className="wl3-hours-week-day-name">{schedule.label}</span>
          <span className={`wl3-hours-week-day-time ${schedule.isClosed ? 'is-closed' : ''}`}>
            {schedule.hours}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const Contact = () => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const allStores = config.stores || [];
  const { colors } = useTheme();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const buttonColor = colors.button;
  const primaryColor = colors.primary;

  const socialLinks = useMemo(() => {
    const networks = config.socialNetworks || {};
    return [
      networks.instagram && { href: networks.instagram, label: 'Instagram', icon: Instagram },
      networks.facebook && { href: networks.facebook, label: 'Facebook', icon: Facebook },
      networks.youtube && { href: networks.youtube, label: 'YouTube', icon: Youtube },
      networks.linkedin && { href: networks.linkedin, label: 'LinkedIn', icon: Linkedin },
      networks.twitter && { href: networks.twitter, label: 'X (Twitter)', icon: Twitter },
      networks.tiktok && { href: networks.tiktok, label: 'TikTok', icon: null },
    ].filter(Boolean) as Array<{ href: string; label: string; icon: typeof Instagram | null }>;
  }, [config.socialNetworks]);

  const hasSocialBlock = socialLinks.length > 0 || !!config.email?.trim();

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const handleOpenMaps = async (targetStore: Store) => {
    if (!userLocation) {
      const location = await requestUserLocation();
      if (location) setUserLocation(location);
    }
    openMapsDirections(targetStore);
  };

  return (
    <Layout3Root withHeader>
      <Header config={config} />

      {/* Cabeçalho — mesmo padrão de Serviços */}
      <section className="wl3-section wl3-section-muted">
        <div className="wl3-container">
          <div className="wl3-section-header">
            <div>
              <p className="wl3-eyebrow">Contato</p>
              <h1 className="wl3-h2 mt-1">Fale conosco</h1>
              <p className="wl3-lead mt-1 max-w-lg text-sm">
                Tire dúvidas, agende uma visita ou fale com nossa equipe.
              </p>
            </div>
          </div>

          {config.about && (
            <div className="wl3-card p-6 md:p-8">
              <p className="text-[var(--wl3-muted)] leading-relaxed whitespace-pre-line">{config.about}</p>
            </div>
          )}
        </div>
      </section>

      {/* Visite — bloco principal */}
      <section className="wl3-section bg-white">
        <div className="wl3-container space-y-12">
          {allStores.map((item, index) => {
            const schedules = hasOpeningHoursData(item.openingHours)
              ? formatOpeningHours(item.openingHours)
              : [];
            const status = hasOpeningHoursData(item.openingHours)
              ? getStoreStatus(item.openingHours)
              : null;
            const distance = getDistanceToStore(userLocation, item);

            return (
              <div key={item._id || index}>
                <div className="wl3-section-header !mb-6">
                  <div>
                    <p className="wl3-eyebrow">Visite-nos</p>
                    <h2 className="wl3-h2 mt-1">{item.company}</h2>
                    {item.place?.address && (
                      <p className="wl3-lead text-sm mt-1">{item.place.address}</p>
                    )}
                  </div>
                  {distance !== null && (
                    <span
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full text-white shrink-0"
                      style={{ backgroundColor: colors.secondary }}
                    >
                      <Navigation className="h-3.5 w-3.5" />
                      {formatDistance(distance)}
                    </span>
                  )}
                </div>

                <div className="wl3-contact-store-card">
                  {item.frontage && (
                    <div className="wl3-contact-store-banner">
                      <img
                        src={item.frontage}
                        alt={`Fachada ${item.company}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="wl3-contact-store-body space-y-6">
                    <div className="wl3-trust-grid sm:grid-cols-3">
                      {item.phone?.ios && (
                        <a href={item.phone.ios} className="wl3-trust-item group hover:shadow-md transition-shadow">
                          <div className="wl3-trust-icon" style={{ backgroundColor: `${buttonColor}12`, color: buttonColor }}>
                            <Phone className="h-5 w-5" />
                          </div>
                          <h3 className="font-semibold text-[var(--wl3-text)] text-sm">Telefone</h3>
                          <p className="text-sm text-[var(--wl3-muted)]">{item.phone.formatted}</p>
                          <span className="inline-flex items-center gap-1 text-xs font-medium mt-1" style={{ color: buttonColor }}>
                            Ligar <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </a>
                      )}

                      {item.whatsapp?.ios && (
                        <button
                          type="button"
                          onClick={() => openWhatsApp(item.whatsapp?.ios)}
                          className="wl3-trust-item group hover:shadow-md transition-shadow text-left w-full"
                        >
                          <div className="wl3-trust-icon" style={{ backgroundColor: '#22c55e18', color: '#16a34a' }}>
                            <MessageCircle className="h-5 w-5" />
                          </div>
                          <h3 className="font-semibold text-[var(--wl3-text)] text-sm">WhatsApp</h3>
                          <p className="text-sm text-[var(--wl3-muted)]">{item.whatsapp.formatted}</p>
                          <span className="inline-flex items-center gap-1 text-xs font-medium mt-1 text-[#16a34a]">
                            Mensagem <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </button>
                      )}

                      {item.place && (
                        <button
                          type="button"
                          onClick={() => handleOpenMaps(item)}
                          className="wl3-trust-item group hover:shadow-md transition-shadow text-left w-full"
                        >
                          <div className="wl3-trust-icon" style={{ backgroundColor: '#ef444418', color: '#dc2626' }}>
                            <MapPin className="h-5 w-5" />
                          </div>
                          <h3 className="font-semibold text-[var(--wl3-text)] text-sm">Localização</h3>
                          <p className="text-sm text-[var(--wl3-muted)] line-clamp-2">{item.place.address}</p>
                          <span className="inline-flex items-center gap-1 text-xs font-medium mt-1 text-[#dc2626]">
                            Rotas <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </button>
                      )}
                    </div>

                    {schedules.length > 0 && status && (
                      <OpeningHoursWeek schedules={schedules} status={status} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Redes sociais */}
      {hasSocialBlock && (
        <section className="wl3-section wl3-section-muted">
          <div className="wl3-container">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="wl3-eyebrow">Redes sociais</p>
              <h2 className="wl3-h2 mt-2">Conecte-se conosco</h2>
              <p className="wl3-lead text-sm mt-2">
                Acompanhe novidades, promoções e lançamentos
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3">
              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wl3-social-link"
                  >
                    {Icon ? (
                      <Icon className="h-4 w-4" style={{ color: buttonColor }} />
                    ) : (
                      <span className="text-xs font-bold" style={{ color: buttonColor }}>TT</span>
                    )}
                    {link.label}
                  </a>
                );
              })}

              {config.email?.trim() && (
                <a href={`mailto:${config.email}`} className="wl3-social-link">
                  <Mail className="h-4 w-4" style={{ color: buttonColor }} />
                  {config.email}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA — mesmo padrão de Serviços / home */}
      <section className={`wl3-section bg-white pb-16 ${hasSocialBlock ? '' : 'wl3-section-muted'}`}>
        <div className="wl3-container">
          <div className="wl3-cta" style={{ backgroundColor: primaryColor }}>
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
                  Prefere falar agora?
                </h2>
                <p className="text-white/85 mt-2 max-w-md">
                  Nossa equipe está pronta para atender você no WhatsApp ou telefone.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                <Button
                  size="lg"
                  className="rounded-lg bg-white font-semibold h-12 px-6 hover:bg-white/90"
                  style={{ color: buttonColor }}
                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                >
                  <MessageCircle className="h-5 w-5 mr-2" />
                  WhatsApp
                </Button>
                {store.phone?.formatted && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg font-semibold h-12 px-6 border-white/30 text-white bg-white/10 hover:bg-white/20 hover:text-white"
                    onClick={() => { window.location.href = `tel:${store.phone?.formatted}`; }}
                  >
                    Ligar agora
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </Layout3Root>
  );
};

export default Contact;
